// sdpShared.jsx — helper & komponen bersama untuk TicketDetailDrawer / AssetDetailDrawer.
import React, { useEffect, useRef, useState } from "react";
import { X, RefreshCw, AlertTriangle } from "lucide-react";

export const API_BASE = window.__API_BASE__ || "";
export const getToken = () => localStorage.getItem("auth_token") || localStorage.getItem("token") || "";

export const authGetJson = async (path) => {
  const r = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
};

export const dv = (v) => {
  if (v == null || v === "") return "";
  if (typeof v !== "object") return String(v);
  return v.display_value || v.name || v.email_id || v.value || "";
};
export const who = (u) => {
  if (!u) return "";
  if (typeof u === "string") return u;
  return [u.name, u.email_id ? `<${u.email_id}>` : ""].filter(Boolean).join(" ");
};
export const listWho = (a) => (Array.isArray(a) ? a.map(who).filter(Boolean).join(", ") : who(a));
export const isEmptyHtml = (h) => !h || !String(h).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
export const fmtSize = (b) => {
  const n = Number(b && b.value != null ? b.value : b);
  if (!n) return dv(b);
  if (n > 1048576) return (n / 1048576).toFixed(1) + " MB";
  return Math.max(1, Math.round(n / 1024)) + " KB";
};

export function HtmlFrame({ html }) {
  const ref = useRef(null);
  const fit = () => {
    try {
      const d = ref.current && ref.current.contentDocument;
      if (d && d.documentElement) ref.current.style.height = Math.max(40, d.documentElement.scrollHeight + 4) + "px";
    } catch (e) { /* ignore */ }
  };
  const doc =
    '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">' +
    "<style>body{margin:0;font:13px/1.5 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1e293b;word-wrap:break-word;overflow-wrap:anywhere}" +
    "img{max-width:100%;height:auto}table{max-width:100%}pre{white-space:pre-wrap}blockquote{margin:4px 0 4px 8px;padding-left:8px;border-left:3px solid #cbd5e1;color:#475569}</style>" +
    "</head><body>" + (html || "") + "</body></html>";
  return (
    <iframe
      ref={ref}
      title="content"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={doc}
      onLoad={() => { fit(); setTimeout(fit, 400); setTimeout(fit, 1500); }}
      style={{ width: "100%", border: "none", height: 60, display: "block" }}
    />
  );
}

export const S = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(15,23,42,.35)", zIndex: 1000 },
  panel: {
    position: "fixed", top: 0, right: 0, bottom: 0, width: "min(760px, 100vw)", background: "#f8fafc",
    zIndex: 1001, boxShadow: "-8px 0 24px rgba(15,23,42,.18)", display: "flex", flexDirection: "column",
  },
  head: { padding: "14px 18px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 12, alignItems: "flex-start" },
  body: { flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 14 },
  card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 },
  h: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "#64748b", margin: "0 0 10px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px 16px" },
  lbl: { fontSize: 11, color: "#64748b" },
  val: { fontSize: 13, color: "#0f172a", fontWeight: 500, wordBreak: "break-word" },
  iconBtn: { border: "1px solid #e2e8f0", background: "#fff", borderRadius: 8, padding: 6, cursor: "pointer", display: "flex", alignItems: "center" },
  badge: { display: "inline-block", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999 },
  item: { borderTop: "1px solid #f1f5f9", paddingTop: 10, marginTop: 10 },
  meta: { fontSize: 12, color: "#475569", marginBottom: 6, display: "flex", flexWrap: "wrap", gap: "2px 10px" },
  muted: { fontSize: 12, color: "#94a3b8" },
  err: { fontSize: 12, color: "#b91c1c", display: "flex", gap: 6, alignItems: "center" },
  tab: { border: "none", background: "none", padding: "6px 10px", fontSize: 13, cursor: "pointer", borderBottom: "2px solid transparent", color: "#475569" },
  tabOn: { borderBottom: "2px solid rgb(var(--brand-600))", color: "rgb(var(--brand-700))", fontWeight: 600 },
  attBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #e2e8f0", background: "#f8fafc", borderRadius: 8,
    padding: "4px 10px", fontSize: 12, color: "rgb(var(--brand-700))", cursor: "pointer", margin: "4px 6px 0 0", maxWidth: "100%",
  },
};

export const statusColor = (s) => {
  const x = String(s || "").toLowerCase();
  if (/closed|resolved|in use/.test(x)) return { background: "#dcfce7", color: "#166534" };
  if (/cancel|disposed|expired/.test(x)) return { background: "#fee2e2", color: "#991b1b" };
  if (/hold|repair|store/.test(x)) return { background: "#fef3c7", color: "#92400e" };
  return { background: "#dbeafe", color: "#1e40af" };
};

export function Field({ label, value }) {
  if (value == null || value === "" || value === "null") return null;
  return (
    <div>
      <div style={S.lbl}>{label}</div>
      <div style={S.val}>{value}</div>
    </div>
  );
}

// Unduh file via proxy backend (/api/sdp/download) → blob → simpan.
export async function downloadSdpFile({ module, id, att }) {
  const name = att.name || att.file_name || "attachment";
  const qs = new URLSearchParams({ module, id: String(id), name });
  if (att.content_url) qs.set("url", att.content_url);
  if (att.id) qs.set("att", String(att.id));
  const r = await fetch(`${API_BASE}/api/sdp/download?${qs.toString()}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error || `HTTP ${r.status}`);
  }
  const blob = await r.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}

export function Attachments({ list, module, id }) {
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState("");
  if (!Array.isArray(list) || !list.length) return null;
  const go = async (a, i) => {
    setErr(""); setBusy((b) => ({ ...b, [i]: true }));
    try { await downloadSdpFile({ module, id, att: a }); }
    catch (e) { setErr(`${a.name || "attachment"}: ${e.message}`); }
    setBusy((b) => ({ ...b, [i]: false }));
  };
  return (
    <div style={{ marginTop: 8 }}>
      {list.map((a, i) => (
        <button key={a.id || i} style={S.attBtn} onClick={() => go(a, i)} disabled={!!busy[i]} title="Download">
          <span>{busy[i] ? "⏳" : "⬇"}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name || a.file_name || "attachment"}</span>
          {a.size ? <span style={{ color: "#94a3b8" }}>({fmtSize(a.size)})</span> : null}
        </button>
      ))}
      {err && <div style={{ ...S.err, marginTop: 6 }}><AlertTriangle size={14} /> {err}</div>}
    </div>
  );
}

// Kerangka panel kanan (backdrop, header, tombol reload/close, Esc).
export function DrawerShell({ label, top, title, loading, onReload, onClose, children }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <>
      <div style={S.backdrop} onClick={onClose} />
      <div style={S.panel} role="dialog" aria-label={label}>
        <div style={S.head}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "#64748b", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{top}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginTop: 4, wordBreak: "break-word" }}>{title}</div>
          </div>
          <button style={S.iconBtn} onClick={onReload} title="Reload from SDP" disabled={loading}>
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button style={S.iconBtn} onClick={onClose} title="Close (Esc)"><X size={16} /></button>
        </div>
        <div style={S.body}>{children}</div>
      </div>
    </>
  );
}
